import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { useAppearance } from "@/domain/user/components/appearance-context";
import { adjustDecimalPrecision, parseNumber, toNumber } from "@/lib/decimal-utils";
import {
  CompactSelection,
  DrawHeaderCallback,
  EditableGridCell,
  GridCell,
  GridCellKind,
  GridSelection,
  Item,
  Theme,
} from "@glideapps/glide-data-grid";

const ROW_HEIGHT = 34;
const HEADER_HEIGHT = 36;
const PADDING_ROW_COUNT = 5;
const OVERSCROLL = 10;
const HORIZONTAL_SCROLLBAR_HEIGHT = 17;

export type DataGridData = {
  [key: string]: {
    value: string | number;
    displayData?: string;
    themeOverride?: Partial<Theme>;
    displayedPrecision?: number | null;
    isReadOnly?: boolean;
  };
};

export const useDataGridConfig = (config: {
  data: DataGridData[];
  columns: string[];
  columnIds?: string[];
  readOnlyColumns: string[];
  readOnlyRows: string[];
  handleCellSelection: (cell: { rowId: string; columnId: string; value: string | number }) => void;
  handleCellUpdate?: (cell: { rowId: string; columnId: string; value: string | number | null }) => void;
  defaultSelection?: GridSelection;
  allowNegativeValues?: boolean;
  handleSelectionChange?: (selection: GridSelection) => void;
  rows?: number;
  showUnitInHeader?: boolean;
  unit?: string | ((columnIndex: number, columnId: string) => string);
  trailingRowHint?: string;
  placeholderColumnIds?: string[];
  fitsContent: boolean;
  hasTrailingRow: boolean;
}) => {
  const {
    data,
    columns,
    columnIds,
    readOnlyColumns,
    readOnlyRows,
    handleCellSelection,
    handleCellUpdate,
    allowNegativeValues,
    handleSelectionChange,
    defaultSelection,
    showUnitInHeader = false,
    unit,
    trailingRowHint,
    placeholderColumnIds,
    fitsContent,
    hasTrailingRow,
  } = config;
  const { fontSize } = useAppearance();

  const theme = {
    horizontalBorderColor: "rgba(115, 116, 131, 0.16)",
    headerBottomBorderColor: "rgba(115, 116, 131, 0.16)",
    textHeader: "#000000",
    headerFontStyle: fontSize === "small" ? "600 10px" : fontSize === "large" ? "600 14px" : "600 12px",
    baseFontStyle: fontSize === "small" ? "10px" : fontSize === "large" ? "14px" : "12px",
    bgCell: "#fafafa",
    bgHeader: "#efefef",
  };

  const calculateFirstColumnWidth = useMemo(() => {
    if (data.length === 0) {
      return 200;
    }
    const maxTextLength = Math.max(
      ...data.map((row) => {
        const text = String(row?.firstColumn?.displayData || row?.firstColumn?.value || "");
        return text.length;
      }),
    );
    const baseWidth = Math.max(200, maxTextLength * 7 + 10);
    return Math.min(baseWidth, 600);
  }, [data]);

  const formattedColumns = columns.map((column, idx) => ({
    id: columnIds?.[idx] ?? column.toUpperCase(),
    title: column,
    width: idx === 0 ? calculateFirstColumnWidth : 150,
  }));

  const getCellContent = useCallback(
    (cell: Item): GridCell => {
      const [col, row] = cell;
      const dataRow = data[row];
      const isFirstColumn = col === 0;
      const isReadOnly = readOnlyColumns.includes(formattedColumns[col].id.toUpperCase()) || isFirstColumn;
      const isReadOnlyRow = readOnlyRows.includes(String(dataRow?.firstColumn?.value ?? "").toUpperCase());

      if (isFirstColumn) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          readonly: true,
          displayData: String(dataRow?.firstColumn?.displayData || ""),
          data: String(dataRow?.firstColumn?.value || ""),
          themeOverride: dataRow?.firstColumn?.themeOverride,
        };
      }

      if (placeholderColumnIds?.includes(formattedColumns[col].id)) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          readonly: true,
          displayData: "",
          data: "",
          themeOverride: { bgCell: "#fafafa" },
        };
      }

      const isFirstColumnEmpty = dataRow?.firstColumn?.value === "";
      if (isFirstColumnEmpty) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          readonly: true,
          displayData: "",
          data: "",
          themeOverride: {},
        };
      }

      // Empty row
      if (!dataRow) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: false,
          readonly: true,
          displayData: "",
          data: "",
        };
      }

      const columnId = formattedColumns[col].id;
      const d = dataRow[columnId as keyof typeof dataRow];
      const value = d?.value ?? 0;
      const displayData = d?.displayData;
      const storedPrecision = d?.displayedPrecision;
      const isCellReadOnly = d?.isReadOnly ?? false;

      const dataToShow =
        displayData !== undefined
          ? displayData
          : typeof value === "number"
            ? storedPrecision !== undefined && storedPrecision !== null
              ? adjustDecimalPrecision(value, storedPrecision)
              : value.toLocaleString("de-DE", { maximumFractionDigits: 6 })
            : "0";

      const shouldBeReadOnly = isReadOnly || isReadOnlyRow || isCellReadOnly;

      return {
        kind: GridCellKind.Number,
        allowOverlay: shouldBeReadOnly ? false : true,
        readonly: shouldBeReadOnly,
        displayData: dataToShow,
        data: toNumber(value) || 0,
        contentAlign: isFirstColumn ? "left" : "right",
        decimalSeparator: ",",
        thousandSeparator: ".",
        themeOverride: { bgCell: "white", ...d?.themeOverride },
      };
    },
    [data, formattedColumns, readOnlyColumns, readOnlyRows, placeholderColumnIds],
  );

  const getCellValue = useCallback(
    (cell: Item) => {
      const [col, row] = cell;
      const columnId = formattedColumns[col].id;
      const dataRow = data[row];
      if (!dataRow) {
        return {
          rowId: "",
          columnId,
          value: "",
        };
      }
      const d = dataRow[columnId as keyof typeof dataRow];
      return {
        rowId: dataRow?.firstColumn?.value,
        columnId: columnId,
        value: d?.value,
      };
    },
    [data, formattedColumns],
  );

  const onCellEdited = useCallback(
    (cell: Item, newValue: EditableGridCell) => {
      const [col, row] = cell;
      const key = formattedColumns[col].id;
      if (data[row] && key) {
        let newValueData = newValue.data as string | number;

        if (typeof newValueData === "string") {
          const parsedNumber = parseNumber(newValueData);
          newValueData = parsedNumber;
        }

        if (typeof newValueData === "number" && newValueData < 0 && !allowNegativeValues) {
          toast.error("Eingabefehler", { description: "Der Wert darf nicht negativ sein." });
          return;
        }
        if (data[row][key as keyof (typeof data)[number]]) {
          (data[row][key as keyof (typeof data)[number]] as { value: string | number }).value = newValueData;
        }
        if (handleCellUpdate) {
          handleCellUpdate({
            rowId: String(data[row].firstColumn?.value),
            columnId: key.toUpperCase(),
            value: typeof newValueData === "number" ? newValueData : null,
          });
        }
      }
    },
    [data, formattedColumns, handleCellUpdate, allowNegativeValues],
  );

  const [selection, setSelection] = useState<GridSelection>(
    defaultSelection ?? {
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
    },
  );

  const onSelectionChange = useCallback(
    (newSel: GridSelection) => {
      const currentCell = newSel.current?.cell;

      if (currentCell) {
        const cellValue = getCellValue(currentCell);
        handleCellSelection({
          ...cellValue,
          rowId: String(cellValue.rowId),
        });
      }
      setSelection({
        ...newSel,
      });
      if (handleSelectionChange) {
        handleSelectionChange(newSel);
      }
    },
    [handleCellSelection, getCellValue, handleSelectionChange],
  );

  // Typing to satisfy the type checker
  type True = true;
  type RowSelect = "none" | "single" | "multi";
  type RangeSelect = "rect";

  const drawHeader: DrawHeaderCallback | undefined =
    showUnitInHeader && unit
      ? (args, draw) => {
          const { columnIndex, ctx, rect, theme } = args;
          const column = formattedColumns[columnIndex];
          if (!column) {
            draw();
            return;
          }

          const title = column.title;
          const padding = 8;
          const titleX = rect.x + padding;
          const titleY = rect.y + rect.height / 2;

          ctx.fillStyle = theme.bgHeader;
          ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

          ctx.fillStyle = theme.textHeader;
          ctx.font = theme.headerFontStyle;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(title, titleX, titleY);

          if (columnIndex > 0) {
            const unitY = rect.y + rect.height / 2;
            const unitPadding = 8;
            const unitX = rect.x + rect.width - unitPadding;
            const unitText = typeof unit === "function" ? unit(columnIndex, column.id) : unit;
            ctx.fillStyle = "#9ca3af";
            ctx.font = `10px ${theme.headerFontStyle.split(" ")[1] || "sans-serif"}`;
            ctx.textAlign = "right";
            ctx.textBaseline = "middle";
            ctx.fillText(unitText, unitX, unitY);
          }

          if (theme.headerBottomBorderColor) {
            ctx.fillStyle = theme.headerBottomBorderColor;
            ctx.fillRect(rect.x, rect.y + rect.height - 1, rect.width, 1);
          }
        }
      : undefined;

  const rows = config.rows ?? (fitsContent ? data.length : data.length + PADDING_ROW_COUNT);
  const visibleRowCount = rows + (hasTrailingRow ? 1 : 0);

  const defaultDataGridConfig = {
    smoothScrollX: true,
    smoothScrollY: true,
    scaleToRem: false,
    overscrollX: OVERSCROLL,
    overscrollY: fitsContent ? 0 : OVERSCROLL,
    rowHeight: ROW_HEIGHT,
    headerHeight: HEADER_HEIGHT,
    getCellsForSelection: true as True,
    width: "100%",
    columns: formattedColumns,
    getCellContent,
    rows,
    rowSelect: "multi" as RowSelect,
    height: fitsContent ? HEADER_HEIGHT + visibleRowCount * ROW_HEIGHT + HORIZONTAL_SCROLLBAR_HEIGHT : "100%",
    className: fitsContent ? "overflow-y-hidden!" : undefined,
    onCellEdited,
    gridSelection: selection,
    onGridSelectionChange: onSelectionChange,
    onPaste: true,
    // Tab must leave the grid (WCAG 2.1.1) — cell movement stays on the arrow keys
    keybindings: { search: true, goRightCell: "ArrowRight", goLeftCell: "ArrowLeft" },
    theme,
    rangeSelect: "rect" as RangeSelect,
    freezeColumns: 1,
    trailingRowOptions: {
      tint: true,
      sticky: true,
      ...(trailingRowHint && { hint: trailingRowHint, addIcon: "addIcon" }),
    },
    ...(drawHeader && { drawHeader }),
  };

  return {
    defaultDataGridConfig,
    getCellContent,
    onCellEdited,
    onSelectionChange,
    theme,
  };
};
